// The voice bar, Inkglish's one shape: rounded vertical bars for today's plan and for the week.
// Ink is done, amber is now, gray is still to come. Icon "voice" is the same shape.
import { cx } from '@/lib/cx';

export type VoiceBar = {
  /** Height from 0 to 1. Zero still draws a dot. */
  value: number;
  state: 'done' | 'now' | 'rest';
  /** Under the bar in the week chart: M, T, W… */
  label?: string;
  /** Above the bar in the week chart: its count. */
  top?: string;
};

type VoiceBarsProps = {
  bars: readonly VoiceBar[];
  /** What the bars say, for screen readers: "1 of 5 done". */
  label: string;
  /** Week chart: bars stand on a line, spread across the width, with a label under each. */
  week?: boolean;
  /** Week chart with counts: taller, wider bars (new mistakes per week). */
  tall?: boolean;
};

/** The tallest bar in a tall chart, in px: room is left above it for its count. */
const TALL = 88;

export function VoiceBars({ bars, label, week, tall }: VoiceBarsProps) {
  return (
    <div
      className={cx('voice-bars', week && 'week', tall && 'tall')}
      role="img"
      aria-label={label}
      style={week ? { gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))` } : undefined}
    >
      {bars.map((b, i) => {
        const value = Math.max(0, Math.min(1, b.value));
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional (plan steps, weekdays).
          <span key={i} className={cx('vb', b.state, value === 0 && 'zero')}>
            <span className="vb-box">
              {week && b.top !== undefined && <span className="vb-top">{b.top}</span>}
              <i style={{ height: tall ? `${Math.round(value * TALL)}px` : `${Math.round(value * 100)}%` }} />
            </span>
            {week && b.label}
          </span>
        );
      })}
    </div>
  );
}

/** Voice-shaped heights, so the plan reads as a voice rather than a progress bar. */
const PLAN_SHAPE = [0.4, 0.7, 0.95, 0.5, 0.75];

/** One bar per step of the day's plan: done steps in ink, the next in amber. */
export function planBars(done: number, total: number): VoiceBar[] {
  return Array.from({ length: total }, (_, i) => ({
    value: PLAN_SHAPE[i % PLAN_SHAPE.length] ?? 1,
    state: i < done ? 'done' : i === done ? 'now' : 'rest',
  }));
}

export type WeekDay = { label: string; minutes: number; today?: boolean };

/** The last 7 days, each bar as tall as the minutes practiced. Today is amber. */
export function weekBars(days: readonly WeekDay[]): VoiceBar[] {
  const most = Math.max(1, ...days.map((d) => d.minutes));
  return days.map((d) => ({
    value: d.minutes / most,
    state: d.today ? 'now' : d.minutes > 0 ? 'done' : 'rest',
    label: d.label,
  }));
}
